import React, { useState, useEffect, useRef } from 'react';
import { getDocumentAsBlob } from '../services/wordHelper';
import type { TemplateContext } from '../App';
import { getAuthHeaders, API_URL } from '../services/auth';
import './SaveTemplate.css';

/**
 * Module-level auto-save guard — deliberately NOT a useRef.
 *
 * React StrictMode (dev) mounts every component twice: mount → unmount →
 * mount. A per-instance useRef is re-created on the second mount, so the
 * auto-save-on-mount effect fires once per instance = two version uploads.
 * That double-fire is benign for *creating* versions (the server dedups),
 * but it breaks conflict handling: the conflicting call's 409 is delivered
 * to the already-unmounted first instance, while the surviving instance
 * only ever sees the second call's duplicate-blocked response. Result: the
 * version-conflict panel can never appear.
 *
 * A module-level record survives the unmount/remount, so the second mount
 * recognises its own echo and skips. Keyed by template id + timestamp so a
 * genuine re-save of the same template later still works.
 */
let lastAutoSave: { templateId: number; at: number } | null = null;
const AUTOSAVE_ECHO_WINDOW_MS = 4000;

interface SaveTemplateProps {
  activeTemplate: TemplateContext | null;
  onSaved: (template: TemplateContext) => void;
  /** When true, pretend there's no binding and force the new-template flow.
   *  Set by the BindingBanner's "Save as new" action (orphan/rebind cases)
   *  and by the future "Save as new template" button in this panel. */
  forceSaveAsNew?: boolean;
  /** Lets the user back out of save-as-new mode (clears App.tsx flag). */
  onCancelSaveAsNew?: () => void;
  /**
   * Set when this Save view was entered via the .dpt-import → new-template
   * handoff. Carries:
   *   - suggestedName            → prefilled in the name input
   *   - suggestedDocumentType    → already stamped to dp_docType by the
   *                                parent, surfaced here only for the
   *                                fork-note display
   *   - sourcePublicId           → forwarded as `importPublicId` on the
   *                                create POST so the new Templates row
   *                                inherits the source environment's UUID
   *                                (preserves cross-env identity)
   */
  pendingImport?: {
    suggestedName: string;
    suggestedDocumentType: string;
    sourcePublicId: string | null;
  } | null;
}

export function SaveTemplate({
  activeTemplate,
  onSaved,
  forceSaveAsNew = false,
  onCancelSaveAsNew,
  pendingImport = null,
}: SaveTemplateProps) {
  // Re-read dp_templateId from the actual Word document so a stale (or
  // not-yet-loaded) `activeTemplate` prop can't make us create a duplicate
  // template. The doc's custom properties are the source of truth.
  const [bound, setBound] = useState<TemplateContext | null>(activeTemplate);
  const [boundLoaded, setBoundLoaded] = useState(false);
  // Captured separately from `bound` so we can still display the "forking
  // from X" hint inside save-as-new mode even though we've overridden
  // bound to null for save-routing purposes.
  const [sourceBinding, setSourceBinding] = useState<TemplateContext | null>(null);
  // When the server says "this template is gone" (404) or "you're trying
  // to overwrite the wrong template" (422 PUBLIC_ID_MISMATCH) we fall the
  // panel into a recovery prompt rather than silently failing.
  const [recovery, setRecovery] = useState<
    | { kind: 'orphan-on-save' }
    | { kind: 'public-id-mismatch'; serverPublicId?: string; claimedPublicId?: string }
    | null
  >(null);
  // Optimistic-concurrency conflict (409). Holds the server's reported
  // current version so the recovery panel can offer "save my version on
  // top as v(currentVersion + 1)" vs "reload the server's version".
  const [conflict, setConflict] = useState<
    | { currentVersion: number; lastSavedBy: string; lastSavedAt: string }
    | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    Word.run(async (context) => {
      const props = context.document.properties.customProperties;
      props.load('items');
      await context.sync();
      const idProp = props.items.find((p: any) => p.key === 'dp_templateId');
      const nameProp = props.items.find((p: any) => p.key === 'dp_templateName');
      const versionProp = props.items.find((p: any) => p.key === 'dp_versionNumber');
      const statusProp = props.items.find((p: any) => p.key === 'dp_versionStatus');
      const publicIdProp = props.items.find((p: any) => p.key === 'dp_publicId');
      if (cancelled) return;
      const resolved: TemplateContext | null = idProp?.value
        ? {
            id: parseInt(String(idProp.value), 10),
            name: nameProp?.value || activeTemplate?.name || '',
            versionNumber: versionProp ? parseInt(String(versionProp.value), 10) : 1,
            versionStatus: (statusProp?.value as TemplateContext['versionStatus']) || 'Draft',
            isLocked: activeTemplate?.isLocked || false,
            // Optional — older bindings won't have it. Server-side verify-binding
            // and the version-upload guard both tolerate its absence.
            publicId: publicIdProp?.value ? String(publicIdProp.value) : activeTemplate?.publicId,
          }
        : activeTemplate;
      setSourceBinding(resolved);
      // In save-as-new mode we deliberately drop the binding so the new-
      // template branch fires. The original binding is preserved on
      // sourceBinding so the UI can still say "Forking from X".
      setBound(forceSaveAsNew ? null : resolved);
      setBoundLoaded(true);
    }).catch(() => { if (!cancelled) { setBound(activeTemplate); setBoundLoaded(true); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceSaveAsNew]);

  const isUpdate = !!bound;
  // Initial name prefers: explicit pendingImport (.dpt → new template) →
  // activeTemplate.name (regular save-as-new path) → empty. pendingImport
  // wins because the user just clicked Import expecting to confirm-save
  // that specific template; falling back to activeTemplate.name would
  // mislead them.
  const [templateName, setTemplateName] = useState(
    pendingImport?.suggestedName || activeTemplate?.name || ''
  );
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const autoSaveTriggered = useRef(false);

  // Auto-save when we've confirmed an existing template binding.
  //
  // The fire is SCHEDULED via setTimeout and CANCELLED in the effect
  // cleanup. This is what defeats StrictMode's dev double-mount correctly:
  //   mount1 → schedule → unmount1 → cleanup cancels it (never runs)
  //   mount2 → schedule → (survives) → fires
  // So the save runs exactly once, FROM THE SURVIVING INSTANCE — its
  // setConflict/onSaved reach a live component, so the conflict panel can
  // actually appear. (A plain synchronous handleSave() here would fire
  // from the doomed first instance and the 409 would hit a dead component.)
  //
  // The module-level `lastAutoSave` guard is re-checked at FIRE time (not
  // in the effect body) so it catches any non-StrictMode rapid remount
  // without the doomed instance "claiming" the slot.
  useEffect(() => {
    if (!(boundLoaded && isUpdate && bound && !autoSaveTriggered.current)) return;
    const templateId = bound.id;
    const handle = setTimeout(() => {
      const now = Date.now();
      if (
        lastAutoSave !== null &&
        lastAutoSave.templateId === templateId &&
        now - lastAutoSave.at < AUTOSAVE_ECHO_WINDOW_MS
      ) {
        return; // a recent auto-save for this template already fired
      }
      lastAutoSave = { templateId, at: now };
      autoSaveTriggered.current = true;
      handleSave();
    }, 50);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundLoaded, isUpdate]);

  // `force` = the user explicitly chose "save my version on top anyway"
  // from the conflict panel. It bypasses the server's optimistic-concurrency
  // check + dedup window, so the current document becomes a new version
  // even though it's based on an older one.
  const handleSave = async (force = false) => {
    if (!boundLoaded) {
      // Still resolving template binding — don't risk creating a duplicate.
      return;
    }
    if (force) setConflict(null);
    if (!isUpdate && !templateName.trim()) {
      setMessage({ type: 'error', text: 'Please enter a template name' });
      return;
    }

    // Check document type is selected
    try {
      const hasDocType = await Word.run(async (context) => {
        const props = context.document.properties.customProperties;
        props.load('items');
        await context.sync();
        const docType = props.items.find((p: any) => p.key === 'dp_docType');
        return !!(docType?.value);
      });
      if (!hasDocType) {
        setMessage({ type: 'error', text: 'Please select a Document Type before saving. Go to Placeholders → Document Type section.' });
        return;
      }
    } catch { /* proceed if check fails */ }

    // For new templates: validate name uniqueness and document type uniqueness
    if (!isUpdate) {
      const name = templateName.trim();
      try {
        // Check if template name already exists
        const templatesResp = await fetch(`${API_URL}/templates?search=${encodeURIComponent(name)}`, {
          headers: { ...getAuthHeaders() },
        });
        const templatesData = await templatesResp.json();
        const existing = (templatesData?.data || []).find(
          (t: any) => t.Name.toLowerCase() === name.toLowerCase()
        );
        if (existing) {
          setMessage({ type: 'error', text: `Template name "${name}" is already in use. Please choose a different name.` });
          return;
        }

        // Get document type from Word custom properties
        let docType = '';
        try {
          docType = await Word.run(async (context) => {
            const props = context.document.properties.customProperties;
            props.load('items');
            await context.sync();
            const dt = props.items.find((p: any) => p.key === 'dp_docType');
            return dt?.value || '';
          });
        } catch { /* ignore */ }

        // Check if document type is already used by another template
        if (docType) {
          const allTemplatesResp = await fetch(`${API_URL}/templates`, {
            headers: { ...getAuthHeaders() },
          });
          const allTemplatesData = await allTemplatesResp.json();
          const typeInUse = (allTemplatesData?.data || []).find(
            (t: any) => t.DocumentType?.toLowerCase() === docType.toLowerCase()
          );
          if (typeInUse) {
            setMessage({ type: 'error', text: `Document type "${docType}" is already assigned to template "${typeInUse.Name}". Each template must have a unique document type.` });
            return;
          }
        }
      } catch { /* proceed if validation fails */ }
    }

    setSaving(true);
    setMessage(null);

    try {
      // Step 1: Extract document as .docx blob
      setStep('Extracting document...');
      const blob = await getDocumentAsBlob();

      // Step 2: Upload
      const name = templateName.trim() || bound?.name || 'Untitled';
      const formData = new FormData();
      formData.append('file', blob, `${name}.docx`);

      let response: Response;
      let resultVersionNumber = 1;

      if (isUpdate && bound) {
        // Optimistic concurrency: tell the server which version we started
        // editing from. If somebody else has saved since, we'll get a 409.
        formData.append('baseVersionNumber', String(bound.versionNumber));
        // Cross-env identity guard. The doc-service compares this to the
        // server's Templates.PublicId for the requested id; if they differ
        // it rejects the upload with PUBLIC_ID_MISMATCH so a dev .docx
        // promoted to prod can't silently overwrite a prod template that
        // happens to share the same auto-increment Id.
        if (bound.publicId) {
          formData.append('basePublicId', bound.publicId);
        }
        // "Save anyway" — set only on a deliberate retry from the conflict
        // panel. Tells the server to bypass the concurrency + dedup checks
        // and create a new version on top of whatever it currently has.
        if (force) {
          formData.append('force', 'true');
        }

        if (bound.versionStatus === 'Draft') {
          // Update existing draft — upload new version via doc-service
          setStep(`Updating draft v${bound.versionNumber}...`);
          formData.append('changeNotes', 'Updated from Word Add-in');
          response = await fetch(
            `${API_URL}/doc-service/templates/${bound.id}/versions`,
            {
              method: 'POST',
              headers: { ...getAuthHeaders() },
              body: formData,
            }
          );
          resultVersionNumber = bound.versionNumber;
        } else {
          // No current draft — create a new draft version
          setStep('Creating new draft version...');
          formData.append('changeNotes', 'New draft from Word Add-in');
          response = await fetch(
            `${API_URL}/doc-service/templates/${bound.id}/versions`,
            {
              method: 'POST',
              headers: { ...getAuthHeaders() },
              body: formData,
            }
          );
        }
      } else {
        // Create new template
        setStep('Saving template...');
        formData.append('name', name);
        // When this Save was reached via the .dpt-import → new-template
        // handoff, forward the source env's PublicId so the new Templates
        // row inherits it. Without this, the new template gets a fresh
        // NEWID() and a future re-import from the source would no longer
        // hit Branch A — it would create a duplicate "(Imported)" row.
        if (pendingImport?.sourcePublicId) {
          formData.append('importPublicId', pendingImport.sourcePublicId);
        }
        response = await fetch(`${API_URL}/doc-service/templates`, {
          method: 'POST',
          headers: { ...getAuthHeaders() },
          body: formData,
        });
      }

      const data = await response.json();

      // Optimistic-concurrency conflict: a newer version exists on the
      // server than the one this user started from. Drop into the conflict
      // panel, which gives the user a real choice — reload the server's
      // version, OR save their copy on top anyway (force). The old
      // behaviour (a dead-end "close and reopen" message) only supported
      // the first intent and stranded anyone whose copy was the good one.
      if (response.status === 409 && data.error?.code === 'VERSION_CONFLICT') {
        setConflict({
          currentVersion: data.error.currentVersion,
          lastSavedBy: data.error.lastSavedBy || 'another user',
          lastSavedAt: data.error.lastSavedAt || '',
        });
        return;
      }

      // Cross-env identity guard tripped: the file claims to be template X
      // but the server's PublicId for X belongs to a different template
      // (typical of dev→prod promotion where Ids collide). Drop into a
      // recovery prompt that lets the user either save-as-new or rebind via
      // the banner. We intentionally don't auto-rebind here — the binding
      // banner is the right place for that decision because it has more
      // context (server name, drift, etc.).
      if (response.status === 422 && data.error?.code === 'PUBLIC_ID_MISMATCH') {
        setRecovery({
          kind: 'public-id-mismatch',
          serverPublicId: data.error.serverPublicId,
          claimedPublicId: data.error.claimedPublicId,
        });
        return;
      }

      // Template at the requested id is gone (soft-deleted, never existed
      // in this env, etc.). Offer save-as-new so the user doesn't have to
      // start over manually.
      if (response.status === 404 || (response.status === 410 && data.error?.code === 'TEMPLATE_DELETED')) {
        setRecovery({ kind: 'orphan-on-save' });
        return;
      }

      if (data.success) {
        const templateId = (isUpdate && bound) ? bound.id : data.data?.id;
        const versionNumber = data.data?.versionNumber || resultVersionNumber;
        const phCount = data.data?.placeholders?.length || 0;

        const statusLabel = `Draft v${versionNumber}`;

        setMessage({
          type: 'success',
          text: isUpdate
            ? `${statusLabel} saved! — ${phCount} placeholder${phCount !== 1 ? 's' : ''} extracted.`
            : `Template "${name}" saved! (ID: ${templateId}) Draft v1 — ${phCount} placeholder${phCount !== 1 ? 's' : ''} extracted.`,
        });

        // Navigate back to placeholders quickly for auto-save, 1.5s for manual
        setTimeout(() => {
          onSaved({
            id: templateId,
            name,
            versionNumber,
            versionStatus: 'Draft',
            isLocked: bound?.isLocked || false,
            // Server returns publicId on create; on update we already had it.
            publicId: data.data?.publicId ?? data.data?.PublicId ?? bound?.publicId,
          });
        }, autoSaveTriggered.current ? 500 : 1500);
      } else {
        setMessage({ type: 'error', text: data.error?.message || 'Failed to save template' });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to save. Check if the document is saved to disk first.',
      });
    } finally {
      setSaving(false);
      setStep('');
    }
  };

  // Show a brief loading state while we're checking the doc for an existing
  // template binding — prevents the new-template form from flashing.
  if (!boundLoaded) {
    return (
      <div className="dp-save-template" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: 12, color: '#64748b' }}>Checking document...</div>
      </div>
    );
  }

  // --- Conflict: server has a newer version than the one we started from -
  // The user gets a real choice instead of a dead end:
  //   • Reload — discard local edits, pick up the server's version
  //   • Save mine anyway — force this document on top as v(current+1),
  //     leaving the superseded server version in history. This is the
  //     path for "the server's newer version is corrupt; mine is good".
  if (conflict) {
    const when = conflict.lastSavedAt
      ? new Date(conflict.lastSavedAt).toLocaleString()
      : 'recently';
    return (
      <div className="dp-save-template" style={{ padding: '20px' }}>
        <h3>Version conflict</h3>
        <p className="dp-save-template__desc">
          The server already has <strong>v{conflict.currentVersion}</strong>,
          saved by {conflict.lastSavedBy} ({when}). Your document is based on
          an older version.
        </p>
        <p className="dp-save-template__desc" style={{ fontSize: 11, color: '#64748b' }}>
          Choose how to resolve it:
        </p>
        <button
          className="dp-save-template__btn"
          onClick={() => handleSave(true)}
          disabled={saving}
        >
          {saving ? (step || 'Saving...') : `Save my version on top (creates v${conflict.currentVersion + 1})`}
        </button>
        <button
          className="dp-save-template__linkbtn"
          style={{ marginTop: 10 }}
          disabled={saving}
          onClick={() => {
            // Reloading replaces the document — route through the Templates
            // list so the user explicitly re-opens the server version
            // rather than us silently discarding their open document.
            setConflict(null);
            setMessage({
              type: 'error',
              text: `Open Templates → Edit to load v${conflict.currentVersion} (this discards your current document).`,
            });
          }}
        >
          Discard mine &amp; reload v{conflict.currentVersion} instead
        </button>
        {message && (
          <div className={`dp-save-template__message dp-save-template__message--${message.type}`}>
            {message.text}
          </div>
        )}
      </div>
    );
  }

  // --- Recovery: template gone or PublicId mismatch ---------------------
  // These two states block the normal save path entirely. The user has to
  // explicitly choose "save as new" (or back out) — we don't auto-pivot
  // because that could silently fork what the user thought was an update.
  if (recovery?.kind === 'orphan-on-save') {
    return (
      <div className="dp-save-template" style={{ padding: '20px' }}>
        <h3>Template no longer exists</h3>
        <p className="dp-save-template__desc">
          The template this document is bound to was deleted or never existed
          in this environment. You can save the current document as a new
          template instead.
        </p>
        <button
          className="dp-save-template__btn"
          onClick={() => {
            setRecovery(null);
            setBound(null);            // pretend we never had a binding
            setMessage(null);
            // Auto-save guard: once we drop the binding, the auto-save
            // effect (which only fires on isUpdate=true) won't re-trigger.
          }}
        >
          Save as new template
        </button>
      </div>
    );
  }
  if (recovery?.kind === 'public-id-mismatch') {
    return (
      <div className="dp-save-template" style={{ padding: '20px' }}>
        <h3>Wrong template</h3>
        <p className="dp-save-template__desc">
          This document claims to be the template at id {sourceBinding?.id ?? '?'},
          but that id in this environment belongs to a different template.
          Saving would overwrite it, so we stopped.
        </p>
        <p className="dp-save-template__desc" style={{ fontSize: 11, color: '#64748b' }}>
          Use the banner at the top to rebind to the correct template, or
          save this document as a new template below.
        </p>
        <button
          className="dp-save-template__btn"
          onClick={() => {
            setRecovery(null);
            setBound(null);
            setMessage(null);
          }}
        >
          Save as new template
        </button>
      </div>
    );
  }

  // Auto-save mode: show only a minimal saving indicator, no form
  if (isUpdate && saving && bound) {
    return (
      <div className="dp-save-template" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>{step || 'Saving...'}</div>
        <div style={{ fontSize: 12, color: '#64748b' }}>"{bound.name}" (ID: {bound.id})</div>
      </div>
    );
  }

  // Auto-save completed with message — show result briefly
  if (isUpdate && message && !saving) {
    return (
      <div className="dp-save-template" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div className={`dp-save-template__message dp-save-template__message--${message.type}`}>
          {message.text}
        </div>
      </div>
    );
  }

  // Render heading: "Save as new" surfaces both when explicitly requested
  // (forceSaveAsNew) and when the user pivoted from a recovery prompt
  // (sourceBinding present, bound cleared).
  const isForkingFromExisting = !isUpdate && sourceBinding;

  return (
    <div className="dp-save-template">
      <h3>
        {isUpdate ? 'Update Template'
         : isForkingFromExisting ? 'Save as new template'
         : 'Save Template'}
      </h3>
      {isForkingFromExisting && (
        <div className="dp-save-template__forknote">
          <strong>Forking from {sourceBinding!.name}</strong> (v{sourceBinding!.versionNumber}).
          Pick a new name and a unique Document Type — placeholders and
          fragment references in this document will carry over automatically.
          {onCancelSaveAsNew && (
            <button
              className="dp-save-template__linkbtn"
              onClick={() => { if (onCancelSaveAsNew) onCancelSaveAsNew(); setBound(sourceBinding); }}
            >
              Cancel — update the original instead
            </button>
          )}
        </div>
      )}
      <p className="dp-save-template__desc">
        {isUpdate && bound
          ? `Update "${bound.name}" (ID: ${bound.id})`
          : 'Save the current document as a new template. Placeholders will be automatically extracted.'
        }
      </p>

      {!isUpdate && (
        <div className="dp-save-template__form">
          <label>
            Template Name
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !saving && handleSave()}
              placeholder="e.g. Bond Execution Form"
              disabled={saving}
            />
          </label>
        </div>
      )}

      <button
        className="dp-save-template__btn"
        onClick={() => handleSave()}
        disabled={saving || (!isUpdate && !templateName.trim())}
      >
        {saving
          ? step || 'Saving...'
          : isUpdate && bound
            ? bound.versionStatus === 'Draft'
              ? `Update Draft v${bound.versionNumber}`
              : 'Create New Draft'
            : 'Save Template'
        }
      </button>

      {/* "Save as new" affordance on the regular update view — see Gap 5.
       *  Hidden when forking (we're already in the fork form) or when no
       *  binding (the regular path is already save-as-new). */}
      {isUpdate && bound && !forceSaveAsNew && (
        <button
          className="dp-save-template__linkbtn"
          style={{ marginTop: 8 }}
          onClick={() => {
            setBound(null);
            setMessage(null);
            autoSaveTriggered.current = true; // suppress re-auto-save
          }}
        >
          Save as new template instead
        </button>
      )}

      {message && (
        <div className={`dp-save-template__message dp-save-template__message--${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
}
